/**
 * Sentry 錯誤監控系統初始化與工具函數
 * 
 * 使用前請確保已設定環境變數：
 * - VITE_SENTRY_DSN
 * - VITE_SENTRY_ENVIRONMENT
 * - VITE_SENTRY_RELEASE
 */

import * as Sentry from '@sentry/react';

/**
 * 初始化 Sentry
 * 應該在應用程式啟動時呼叫（main.jsx）
 */
export function initSentry() {
  // 只在 Sentry DSN 存在時初始化
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.log('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
  const release = import.meta.env.VITE_SENTRY_RELEASE || '1.0.0';

  console.log(`[Sentry] Initializing... Environment: ${environment}, Release: ${release}`);

  Sentry.init({
    dsn,
    environment,
    release,
    
    // 效能監控 - 取樣率
    // 生產環境捕捉 10% 的交易以減少成本
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    // Session 重播 - 記錄用戶操作
    // 正常情況下記錄 10%，錯誤時記錄 100%
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // 整合功能
    integrations: [
      // Session 重播（錄製用戶操作）
      Sentry.replayIntegration({
        maskAllText: false,      // 不遮蔽文字（可改為 true 提高隱私）
        blockAllMedia: false,    // 不遮蔽媒體
        maskAllInputs: true,     // 遮蔽所有輸入欄位（保護隱私）
      }),
      
      // 瀏覽器追蹤（已包含 React Router 支援）
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          'localhost',
          'nmn.wedopr.com',
          /^https:\/\/nmn\.wedopr\.com/,
        ],
      }),
    ],
    
    // 過濾敏感資料（在發送前處理）
    beforeSend(event, hint) {
      // 移除 cookies（可能包含敏感資訊）
      if (event.request) {
        delete event.request.cookies;
      }
      
      // 移除可能的密碼欄位
      if (event.request?.data) {
        const data = event.request.data;
        if (typeof data === 'object') {
          delete data.password;
          delete data.confirmPassword;
          delete data.token;
        }
      }
      
      return event;
    },
    
    // 忽略特定錯誤（減少雜訊）
    ignoreErrors: [
      // 瀏覽器擴充套件錯誤
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      
      // Safari 特定錯誤
      'Non-Error promise rejection captured',
      
      // 網路錯誤（通常是用戶網路問題）
      'Network request failed',
      'NetworkError',
      
      // React DevTools
      'Warning: ReactDOM.render',
    ],
    
    // 忽略特定 URL（第三方腳本錯誤）
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      
      // Firefox extensions
      /^resource:\/\//i,
      
      // Google Analytics
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
      
      // Facebook
      /connect\.facebook\.net/i,
      /graph\.facebook\.com/i,
    ],
  });

  console.log('[Sentry] Initialization complete');
}

/**
 * 手動捕捉錯誤
 * 
 * @param {Error} error - 錯誤物件
 * @param {Object} context - 額外的上下文資訊
 * @param {Object} context.tags - 標籤（用於分類和篩選）
 * @param {Object} context.extra - 額外資料（詳細資訊）
 * @param {string} context.level - 嚴重程度 (fatal/error/warning/info/debug)
 * 
 * @example
 * captureError(error, {
 *   tags: {
 *     feature: 'checkout',
 *     payment_method: 'credit_card',
 *   },
 *   extra: {
 *     order_id: '12345',
 *     user_id: 'user-abc',
 *   },
 *   level: 'error',
 * });
 */
export function captureError(error, context = {}) {
  console.error('[Sentry] Capturing error:', error);
  
  Sentry.captureException(error, {
    tags: context.tags || {},
    extra: context.extra || {},
    level: context.level || 'error',
  });
}

/**
 * 捕捉訊息（非錯誤的重要事件）
 * 
 * @param {string} message - 訊息內容
 * @param {string} level - 嚴重程度
 * @param {Object} context - 額外的上下文
 * 
 * @example
 * captureMessage('Payment completed', 'info', {
 *   tags: { payment_method: 'credit_card' },
 *   extra: { amount: 4900 },
 * });
 */
export function captureMessage(message, level = 'info', context = {}) {
  console.log(`[Sentry] Capturing message [${level}]:`, message);
  
  Sentry.captureMessage(message, {
    level,
    tags: context.tags || {},
    extra: context.extra || {},
  });
}

/**
 * 設定用戶資訊
 * 用戶登入後應該呼叫此函數
 * 
 * @param {Object|null} user - 用戶物件（或 null 清除）
 * @param {string} user.id - 用戶 ID
 * @param {string} user.email - Email
 * @param {string} user.name - 姓名
 * 
 * @example
 * // 登入後
 * setUser({ id: '123', email: 'user@example.com', name: 'John' });
 * 
 * // 登出後
 * setUser(null);
 */
export function setUser(user) {
  if (user) {
    console.log('[Sentry] Setting user:', user.id);
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  } else {
    console.log('[Sentry] Clearing user');
    Sentry.setUser(null);
  }
}

/**
 * 添加麵包屑（記錄用戶行為）
 * 有助於理解錯誤發生前的用戶操作
 * 
 * @param {string} message - 麵包屑訊息
 * @param {string} category - 類別（ui/navigation/console/xhr/user）
 * @param {string} level - 嚴重程度
 * @param {Object} data - 額外資料
 * 
 * @example
 * addBreadcrumb('User clicked checkout button', 'ui', 'info');
 * addBreadcrumb('Added item to cart', 'user-action', 'info', {
 *   product_id: '123',
 *   quantity: 2,
 * });
 */
export function addBreadcrumb(message, category = 'custom', level = 'info', data = {}) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * 設定標籤
 * 用於對事件進行分類和篩選
 * 
 * @param {Object} tags - 標籤物件
 * 
 * @example
 * setTags({ feature: 'checkout', version: '1.0.0' });
 */
export function setTags(tags) {
  Sentry.setTags(tags);
}

/**
 * 設定額外上下文
 * 
 * @param {string} name - 上下文名稱
 * @param {Object} context - 上下文資料
 * 
 * @example
 * setContext('cart', {
 *   items_count: 3,
 *   total_amount: 14700,
 * });
 */
export function setContext(name, context) {
  Sentry.setContext(name, context);
}

/**
 * 開始一個新的交易（效能追蹤）
 * 
 * @param {string} name - 交易名稱
 * @param {string} op - 操作類型
 * @returns {Span} - Span 物件
 * 
 * @example
 * const span = startTransaction('checkout-flow', 'task');
 * try {
 *   await processCheckout();
 *   span.setStatus('ok');
 * } catch (error) {
 *   span.setStatus('internal_error');
 *   throw error;
 * } finally {
 *   span.end();
 * }
 */
export function startTransaction(name, op = 'task') {
  // Sentry React SDK 使用 startSpan 而非 startTransaction
  return Sentry.startSpan({ name, op }, (span) => span);
}

/**
 * 包裝 API 請求以自動追蹤錯誤
 * 
 * @param {Function} apiFunction - API 函數
 * @param {Object} options - 選項
 * @returns {Function} - 包裝後的函數
 * 
 * @example
 * const createOrder = wrapApiCall(async (data) => {
 *   const response = await fetch('/api/orders', {
 *     method: 'POST',
 *     body: JSON.stringify(data),
 *   });
 *   return response.json();
 * }, { tags: { api: 'orders' } });
 */
export function wrapApiCall(apiFunction, options = {}) {
  return async (...args) => {
    try {
      return await apiFunction(...args);
    } catch (error) {
      captureError(error, {
        tags: {
          api: 'true',
          ...options.tags,
        },
        extra: {
          args,
          ...options.extra,
        },
      });
      throw error;
    }
  };
}

/**
 * 檢查 Sentry 是否已初始化
 * 
 * @returns {boolean}
 */
export function isSentryInitialized() {
  return !!import.meta.env.VITE_SENTRY_DSN;
}

// 導出 Sentry 物件供進階使用
export { Sentry };
